import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { CurrencyDollar, Eye, EyeSlash } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const { user, loading, register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await register(name, email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#1E3F2D] rounded-2xl mb-4">
            <CurrencyDollar size={28} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Spell FinX
          </h1>
          <p className="text-[#5C5A56] mt-1 text-sm">Start your financial journey</p>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <h2 className="text-xl font-bold text-[#1C1B1A] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Create account</h2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-[#A63C24] text-sm rounded-lg p-3 mb-4" data-testid="register-error">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="register-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Full Name</label>
              <input
                id="register-name"
                name="name"
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label htmlFor="register-email" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Email</label>
              <input
                id="register-email"
                name="email"
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label htmlFor="register-password" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Password</label>
              <div className="relative">
                <input
                  id="register-password"
                  name="password"
                  data-testid="register-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  placeholder="Min 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5A56] hover:text-[#1C1B1A] transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              data-testid="register-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? 'Creating account...' : 'Create account'}
            </button>
          </form>
          
          <p className="text-center text-sm text-[#5C5A56] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1E3F2D] font-semibold hover:underline" data-testid="go-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
